import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Parallax, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import '../assets/css/style2.css';
import '../assets/css/globalStyles.css';

// Import images - updated path to match original structure
import slide01 from '../assets/images/slide01.webp';
import slide01_m from '../assets/images/slide01_m.webp';
import slide02 from '../assets/images/slide02.webp';
import slide02_m from '../assets/images/slide02_m.webp';
import slide03 from '../assets/images/slide03.webp';
import slide03_m from '../assets/images/slide03_m.webp';

const Slider = () => {
  const sliderPrevRef = useRef(null);
  const sliderNextRef = useRef(null);
  const swiperRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Handle responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize(); // check on load
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize swiper when component mounts
  useEffect(() => {
    if (swiperRef.current && swiperRef.current.swiper) {
      // Start autoplay immediately
      swiperRef.current.swiper.autoplay.start();
    }
  }, []);

  // Slides content - updated to match exactly the original HTML
  const slides = [
    {
      desktop: slide01,
      mobile: slide01_m,
      heading: 'SRI LANKAN HERITAGE MEETS BRITISH LUXURY',
      title: {
        desktop: 'A Sanctuary in<br>Nuwara-Eliya',
        mobile: 'A Sanctuary in<br>Nuwara-Eliya'
      },
      link: '/kent/accommodation',
      linkText: 'SEE OUR SUITES',
    },
    {
      desktop: slide02,
      mobile: slide02_m,
      heading: "IT'S A HOME AWAY FROM FROM HOME",
      title: {
        desktop: 'Misty Hills, Perfect Days',
        mobile: 'Misty Hills,<br> Perfect Days'
      },
      link: '/kent/accommodation',
      linkText: 'CHECK ROOMS',
    },
    {
      desktop: slide03,
      mobile: slide03_m,
      heading: 'UNPLUG AND RELAX',
      title: {
        desktop: 'Live the Suite life',
        mobile: 'Live the<br>Suite life'
      },
      link: '/kent/theKentCottage',
      linkText: 'DISCOVER KENT COTTAGE',
    },
  ];

  // Swiper configuration
  const swiperConfig = {
    loop: true,
    speed: 600,
    parallax: true,
    modules: [Navigation, Pagination, Parallax, Autoplay],
    autoplay: {
      delay: 5500,
      disableOnInteraction: false,
    },
    grabCursor: true,
    watchSlidesProgress: true,
    navigation: {
      nextEl: '.slider-next',
      prevEl: '.slider-prev',
    },
    on: {
      init: function() {
        // Force start autoplay on init
        this.autoplay.start();
      },
      progress: function (swiper) {
        const interleaveOffset = 0.5;
        for (let i = 0; i < swiper.slides.length; i++) {
          const slideProgress = swiper.slides[i].progress;
          const innerOffset = swiper.width * interleaveOffset;
          const innerTranslate = slideProgress * innerOffset;
          
          const slideInner = swiper.slides[i].querySelector(".slide-inner");
          if (slideInner) {
            slideInner.style.transform = `translate3d(${innerTranslate}px, 0, 0)`;
          }
        }
      },
      touchStart: function (swiper) {
        for (let i = 0; i < swiper.slides.length; i++) {
          swiper.slides[i].style.transition = "";
        }
      },
      setTransition: function (swiper, speed) {
        for (let i = 0; i < swiper.slides.length; i++) {
          swiper.slides[i].style.transition = speed + "ms";
          const slideInner = swiper.slides[i].querySelector(".slide-inner");
          if (slideInner) {
            slideInner.style.transition = speed + "ms";
          }
        }
      }
    }
  };

  return (
    <header className="slider">
      <div className="main-slider">
        <Swiper 
          {...swiperConfig} 
          className="swiper-wrapper"
          ref={swiperRef}
        >
          {slides.map((slide, index) => (
            <React.Fragment key={index}>
              {/* Show desktop view when not mobile */}
              {!isMobile ? (
                <SwiperSlide className="swiper-slide desktopview">
                  <div 
                    className="slide-inner" 
                    data-background={slide.desktop}
                    style={{ backgroundImage: `url(${slide.desktop})` }}
                  >
                    <div className="container">
                      {/* Modified: Added fontFamily 'Jost' to all headings */}
                      <h6 
                        data-swiper-parallax="100"
                        style={{
                          fontFamily: 'Jost, sans-serif', // Apply Jost font to all headings
                          ...(slide.heading === 'SRI LANKAN HERITAGE MEETS BRITISH LUXURY' ||
                          slide.heading === "IT'S A HOME AWAY FROM FROM HOME"
                            ? { fontSize: '0.8rem' }
                            : {})
                        }}
                      >
                        {slide.heading}
                      </h6>
                      <h1 
                        data-swiper-parallax="200"
                        dangerouslySetInnerHTML={{ __html: slide.title.desktop }}
                      ></h1>
                      <a href={slide.link} data-swiper-parallax="200">{slide.linkText}</a>
                    </div>
                  </div>
                </SwiperSlide>
              ) : (
                <SwiperSlide className="swiper-slide mobileview">
                  <div 
                    className="slide-inner" 
                    data-background={slide.mobile}
                    style={{ backgroundImage: `url(${slide.mobile})` }}
                  >
                    <div className="container">
                      {/* Modified: Added fontFamily 'Jost', reduced font size, reduced line height, removed letter spacing, and unified font size in mobile view */}
                      <h6 
                        data-swiper-parallax="100"
                        style={{
                          fontFamily: 'Jost, sans-serif', // Apply Jost font to all headings
                          lineHeight: '1.2', // Reduced line height to decrease spacing between lines
                          letterSpacing: '0', // Remove gaps between characters
                          fontSize: '0.7rem' // Unified font size to match the third heading
                        }}
                      >
                        {slide.heading}
                      </h6>
                      {/* Modified: Reduced font size of titles in mobile view */}
                      <h1 
                        data-swiper-parallax="200"
                        style={{ fontSize: '1.8rem' }} // Reduced title font size in mobile view
                        dangerouslySetInnerHTML={{ __html: slide.title.mobile }}
                      ></h1>
                      {/* Modified: Reduced button size and height in mobile view, fixed invalid padding */}
                      <a 
                        href={slide.link} 
                        data-swiper-parallax="200"
                        style={{
                          fontSize: '0.7rem', // Reduced button font size
                          padding: '0px 16px' // Fixed invalid negative padding, restored to 6px vertical padding
                        }}
                      >
                        {slide.linkText}
                      </a>
                    </div>
                  </div>
                </SwiperSlide>
              )}
            </React.Fragment>
          ))}
        </Swiper>
        
        {/* Navigation Arrows */}
        <div className="slider-nav">
          <div className="slider-prev" ref={sliderPrevRef}></div>
          <div className="slider-next" ref={sliderNextRef}></div>
        </div>
      </div>
      {/* <div className="scroll-down"></div> */}
    </header>
  );
};

export default Slider;